import { css, html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators/property.js";
import { queryAll } from "lit/decorators/query-all.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import debounce from "lodash-es/debounce";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import { LANGUAGES, TUrl } from "../../api-sdk/types";
import { getImpact } from "../../api-sdk/v3/routes";
import { MissingConfig } from "../../utils/beam-errors";
import "../../shared/components/progress-bar";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { AsyncController } from "../../shared/controllers/async-controller";
import { cssReset } from "../../shared/css-reset";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { enforceConfig } from "../../shared/enforce-config";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { _loading } from "../../shared/fragments/loading-template";
import { localizeUserString } from "../../shared/localize";
import { logger } from "../../utils/logger";
import "../../shared/shoelace-components";
import { syncElementHeights } from "../../shared/sync-element-heights";
import { TId, TNumericId } from "../../shared/types";
import "../beam-partner-logos";
import { partnerLogosConfigDefaults } from "../beam-partner-logos";
import { shareButtonConfigDefaults } from "../../shared/components/share-button";
import { cssResponsiveUtils } from "../../shared/responsive";
import { strings } from "./strings";
import "../../shared/components/share-button";

type RequiredConfig = {
  apiKey: string;
  nonprofitId: TNumericId;
  userId: TId;
};

export class BeamImpactOverview extends LitElement {
  static tagName = "beam-impact-overview";

  @property({ type: String, reflect: true }) public baseUrl: TUrl = DEFAULT_BASE_URL;

  @property({ type: String, reflect: false }) apiKey?: RequiredConfig["apiKey"];

  @property({ type: Number }) storeId?: number;

  @property({ type: String, reflect: true }) nonprofitId?: RequiredConfig["nonprofitId"];

  @property({ type: String, reflect: true }) userId?: RequiredConfig["userId"];

  @property({ type: String }) public lang: LANGUAGES = "en";

  @property({ type: Boolean }) public debug = false;

  @property({ type: Boolean }) public draftConfig = false;

  @property({ type: String }) transactionId?: string;
  /**
   * @deprecated (v1.49.0) promoCodes and discountCodes will now be retrieved on the backend using transactionId
   */
  @property({ type: Object }) public discountCodes?: string[];

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  getImpactData = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "userId", "nonprofitId"], this)) throw new MissingConfig();
    const res = await getImpact({
      baseUrl: this.baseUrl,
      headers: {
        authorization: `Api-Key ${this.apiKey}`,
      },
      queryParams: {
        nonprofitId: this.nonprofitId,
        beamUserId: this.userId,
        personalImpactLimit: 1,
        communityImpactLimit: 1,
        storeId: this.storeId,
        widgetName: WIDGET_NAMES.impact_overview,
        version: "1.0.0",
        draftConfig: this.draftConfig,
        lang: this.configLang,
        discountCodes: this.discountCodes && this.discountCodes.length > 0 ? this.discountCodes : undefined,
        transactionId: this.transactionId ?? undefined,
      },
    });

    return res;
  };

  private impactDataController = new AsyncController<typeof this.getImpactData>(this, this.getImpactData);

  @queryAll(".impact-card-title")
  impactTitleRefs!: HTMLElement[];

  @queryAll(".impact-card-description")
  impactDescriptionRefs!: HTMLElement[];

  @queryAll(".impact-link")
  impactLinkRefs!: HTMLElement[];

  @queryAll(".impact-card-goalCompletionText")
  goalCompletionTextRefs!: HTMLElement[];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.resizeElements);
  }

  async updated(changedProperties: PropertyValues): Promise<void> {
    // Reload nonprofit list on change of any of these props:
    // Also fires on first load as props go from undefined => value
    const requireNewDataProps = ["baseUrl", "storeId", "apiKey", "userId", "lang", "discountCodes"];
    for (const prop of requireNewDataProps) {
      if (changedProperties.has(prop)) {
        await this.impactDataController.exec();
        break;
      }
    }
    this.resizeElements(); // Handle layout shifts immediately
    await this.handleFontLoad(this.resizeElements); // If content change triggered font load, resize again
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.resizeElements);
    document.fonts.removeEventListener("loadingdone", this.resizeElements);
    super.disconnectedCallback();
  }

  /**
   * Safari does not fire loading events on the document FontFaceSet, but it does implement the .ready Promise
   * Call this method to wait for font loads that may be triggered by content changes
   */
  async handleFontLoad<T = any>(callback: () => T | void) {
    if (document.fonts.status === "loading") {
      await document.fonts.ready.catch((e) => logger.error(e));
      return callback();
    }
  }

  resizeElements = debounce(
    () => {
      syncElementHeights(this.impactTitleRefs);
      syncElementHeights(this.impactDescriptionRefs);
      syncElementHeights(this.impactLinkRefs);
      syncElementHeights(this.goalCompletionTextRefs);
    },
    50,
    { maxWait: 50, leading: true }
  );

  renderCard({
    imageUrl,
    title,
    percentFunded,
    goalProgressText,
    description,
    urlCopy,
    impactUrl,
    isImage,
    cardType,
    goalCompletionText,
  }: {
    imageUrl: TUrl;
    title: string;
    percentFunded: number;
    goalProgressText: string;
    description: string;
    urlCopy?: string;
    impactUrl: TUrl;
    isImage: boolean;
    cardType: string;
    goalCompletionText?: string;
  }) {
    return html`
      <div class="impact-card ${isImage ? "" : "icon"}" part="impact-card">
        <div class="${isImage ? "" : "icon-background "}">
          <img src="${imageUrl}" alt="${title}" class="${isImage ? "impact-card-image" : "impact-card-icon"}" />
        </div>
        <div class="${isImage ? "" : "impact-icon-card-content"}" style="margin: 12px;">
          <div class="impact-card-title">${title}</div>
          <p class="impact-card-description">${unsafeHTML(description)}</p>
          <div class="impact-card-goalCompletionText">
            <p>${unsafeHTML(goalCompletionText)}</p>
          </div>
          <div class="impact-card-progress" style="display: flex; align-items: center;">
            <beam-progress-bar
              value="${percentFunded}"
              style="flex: 1 0;"
              class="impact-card-progressbar"
            ></beam-progress-bar>
            <span class="impact-card-progressText" style="flex: 0 1; margin-left: 10px; white-space: nowrap;"
              >${localizeUserString(this.configLang, goalProgressText)}</span
            >
          </div>
          <p class="impact-link" part="impact-link">
            <a href="${impactUrl}" target="_blank" rel="noopener noreferrer">
              ${cardType === "personal"
                ? strings[this.configLang].personalImpactLinkCopy({
                    nonprofitName: urlCopy,
                  })
                : strings[this.configLang].communityImpactLinkCopy({
                    brandName: urlCopy,
                  })}<span class="impact-link-arrow">&nbsp;&rsaquo;</span>
            </a>
          </p>
        </div>
      </div>
    `;
  }

  render() {
    const { data, loading, error } = this.impactDataController;
    if (loading) {
      return _loading();
    }
    if (error) {
      if (this.debug) {
        return _errorMessage({ error });
      }
      return "";
    }
    if (data == null) {
      if (this.debug) {
        return _errorMessage({ error: new Error("No data") });
      }
      return "";
    }
    const brandName = this.impactDataController.data?.chain.name;
    const cardStyle = this.cssVariables["--beam-ImpactOverview-cardStyle"];
    const isImage = cardStyle === "'image'";
    const isSelectedIcon = cardStyle === "'selected_icon'";
    const personalImageUrl = data.personal[0]?.imageUrl || "";
    const communityImageUrl = data.community[0].imageUrl || "";
    const iconUrl = data.personal[0]?.nonprofit.causeIconUrl;
    const selectedIconUrl = data.personal[0]?.nonprofit.causeIconSelectedUrl;
    const personalCardImage = isImage ? personalImageUrl : isSelectedIcon ? selectedIconUrl : iconUrl;
    const communityCardImage = isImage ? communityImageUrl : isSelectedIcon ? selectedIconUrl : iconUrl;

    const shareImage =
      this.impactDataController.data?.promoAssetUrl || this.impactDataController.data?.personalImpactAssetUrl;
    const showShare = this.cssVariables["--beam-ImpactOverview-share-button-display"] !== "none";
    const showShareMobile =
      showShare && this.cssVariables["--beam-ImpactOverview-share-button-display-mobile"] !== "none";
    const showShareDesktop =
      showShare && this.cssVariables["--beam-ImpactOverview-share-button-display-desktop"] !== "none";
    const shareIcon = this.cssVariables["--beam-ImpactOverview-share-button-icon-name"] || "share-2";

    let shareButtonVisibilityClass = "d-none";
    if (showShareMobile) {
      shareButtonVisibilityClass = showShareDesktop ? "d-block" : "d-lg-none";
    } else {
      shareButtonVisibilityClass = showShareDesktop ? "d-none d-lg-block" : "d-none";
    }

    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>

      <div>
        <beam-partner-logos
          partnerLogoUrl="${data.chain.logoUrl}"
          partnerName="${data.chain.name}"
          part="logos"
        ></beam-partner-logos>
      </div>

      <div class="description">
        <div class="title" part="title">
          ${localizeUserString(this.configLang, data.config?.web?.title) ||
          strings[this.configLang].descriptionTitle({ nonprofitName: data.personal[0].nonprofit.name })}
        </div>
        <div class="subtitle" part="subtitle">
          ${localizeUserString(this.configLang, data.config?.web?.description) ||
          strings[this.configLang].descriptionSubtitle({ brandName: data.chain.name })}
        </div>
      </div>

      <div class="${isImage ? "impact-image-cards" : "impact-icon-cards"}" part="impact-cards">
        <div class="impact your-impact ${isImage ? "image-card-container" : "icon-card-container"}">
          ${this.renderCard({
            isImage,
            imageUrl: personalCardImage || "",
            title: strings[this.configLang].yourImpactTitle(),
            percentFunded: data.personal[0]?.impact?.goalProgressPercentage || 0,
            goalProgressText: data.personal[0]?.impact?.goalProgressText,
            description: localizeUserString(this.configLang, data.personal[0]?.impact?.description || ""),
            urlCopy: data.personal[0].nonprofit?.name,
            impactUrl: data.personal[0].nonprofit?.website || "",
            cardType: "personal",
            goalCompletionText: data.personal[0].impact.goalCompletionText,
          })}
        </div>
        <div class="impact community-impact ${isImage ? "image-card-container" : "icon-card-container"}">
          ${this.renderCard({
            isImage,
            imageUrl: communityCardImage || "",
            title: strings[this.configLang].communityImpactTitle(),
            percentFunded: data.community[0].impact?.goalProgressPercentage || 0,
            goalProgressText: data.community[0]?.impact?.goalProgressText,
            description: localizeUserString(this.configLang, data.community[0].impact?.description || ""),
            urlCopy: data.chain.name,
            impactUrl:
              data.store.communityPageURL || data.chain.communityImpactUrl || data.personal[0].nonprofit.website || "",
            cardType: "community",
            goalCompletionText: data.community[0].impact.goalCompletionText,
          })}
        </div>
      </div>

      <div class="footer ${shareButtonVisibilityClass} shareButtonContainer" part="social-share">
        <beam-share-button
          imageUrl="${shareImage}"
          brandName="${brandName}"
          shareIcon="${shareIcon}"
          buttonText="${strings[this.configLang].impactShareButton()}"
          configLang="${this.configLang}"
          baseUrl="${this.baseUrl}"
          storeId="${this.storeId}"
          nonprofitId="${this.nonprofitId}"
          transactionId="${this.transactionId}"
          apiKey="${this.apiKey}"
        ></beam-share-button>
      </div>

      <div id="modal-root"></div>
    `;
  }

  public get cssVariables() {
    const defaults = {
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "inherit",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      ...partnerLogosConfigDefaults,
      ...progressBarConfigDefaults,
      ...shareButtonConfigDefaults,
      ...defineCustomText("--beam-ImpactOverview-title", {
        fontSize: "16px",
        fontWeight: "bold",
        marginTop: "10px",
        textTransform: "capitalize",
      }),
      ...defineCustomText("--beam-ImpactOverview-subtitle", {
        marginTop: "5px",
      }),
      "--beam-ImpactOverview-impactCards-maxWidth": "650px",
      "--beam-ImpactOverview-impactCard-backgroundColor": "inherit",
      "--beam-ImpactOverview-impactCard-foregroundColor": "currentColor",
      "--beam-ImpactOverview-impactCard-borderColor": "currentColor",
      "--beam-ImpactOverview-impactCard-borderRadius": "5px",
      "--beam-ImpactOverview-impactCard-marginTop": "10px",
      ...defineCustomText("--beam-ImpactOverview-impactCard-title", {
        fontSize: "15px",
        fontWeight: "bold",
        letterSpacing: "0.36px",
      }),
      "--beam-ImpactOverview-impactCard-progress-marginTop": "8px",
      ...defineCustomText("--beam-ImpactOverview-impactCard-progressText"),
      ...defineCustomText("--beam-ImpactOverview-impactCard-description", {
        marginTop: "10px",
      }),
      "--beam-ImpactOverview-impactCard-linkArrow-display": "inline",
      "--beam-ImpactOverview-impactCard-link-color": "inherit",
      ...defineCustomText("--beam-ImpactOverview-impactCard-link", {
        marginTop: "10px",
        textDecoration: "underline",
        textTransform: "capitalize",
      }),
      ...defineCustomText("--beam-ImpactOverview-goalCompletionText", {
        marginTop: "5px",
        fontWeight: "600",
        textTransform: "capitalize",
        fontSize: "13px",
      }),
      "--beam-ImpactOverview-share-button-display": "none",
      "--beam-ImpactOverview-share-button-display-mobile": "inherit",
      "--beam-ImpactOverview-share-button-display-desktop": "inherit",
      "--beam-ImpactOverview-image-display": "block",
      "--beam-ImpactOverview-cardStyle": "'image'", // icon, selected_icon, image
      "--beam-ImpactOverview-iconBackground-width": "75px",
      "--beam-ImpactOverview-iconBackground-height": "75px",
      "--beam-ImpactOverview-iconBackground-borderRadius": "50px",
      "--beam-ImpactOverview-icon-padding": "0px",
      "--beam-ImpactOverview-iconBackground-background": "#f3f3f3",
      "--beam-ImpactOverview-impactCard-shareButton-background": "#fff",
      "--beam-ImpactOverview-impactCard-shareButton-boxShadow": "0px -20px 20px 0px rgb(0 0 0 / 2%)",
    };

    const remoteConfig = this.impactDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    cssResponsiveUtils,
    css`
      :host {
        display: block;
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        max-width: var(--beam-ImpactOverview-impactCards-maxWidth, 650px);
        word-break: normal;
      }
      .icon {
        display: flex;
        align-items: center;
      }
      .icon-background {
        width: var(--beam-ImpactOverview-iconBackground-width, 75px);
        height: var(--beam-ImpactOverview-iconBackground-height, 75px);
        border-radius: var(--beam-ImpactOverview-iconBackground-borderRadius, 50px);
        padding: var(--beam-ImpactOverview-icon-padding, 0px);
        margin: 8px;
        overflow: hidden;
        background: var(--beam-ImpactOverview-iconBackground-background, #f3f3f3);
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .impact-icon-card-content {
        flex: 3 1 75%;
      }
      .impact-image-cards {
        max-width: var(--beam-ImpactOverview-impactCards-maxWidth);
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        margin: 10px 0;
      }
      .impact-icon-cards {
        max-width: var(--beam-ImpactOverview-impactCards-maxWidth);
        margin: 10px 0;
      }
      .impact-card {
        padding-bottom: 5px;
        margin-top: var(--beam-ImpactOverview-impactCard-marginTop);
        background-color: var(--beam-ImpactOverview-impactCard-backgroundColor);
        border: 1px solid var(--beam-ImpactOverview-impactCard-borderColor);
        border-radius: var(--beam-ImpactOverview-impactCard-borderRadius);
        overflow: hidden; /* maintain clean border-radius */
        color: var(--beam-ImpactOverview-impactCard-foregroundColor);
      }
      .impact-card-image {
        object-fit: cover;
        height: 110px;
        width: 100%;
        display: var(--beam-ImpactOverview-image-display);
      }
      .impact-card-icon {
        flex: 1 1 10%;
        max-width: 50%;
        display: var(--beam-ImpactOverview-image-display);
      }
      .image-card-container {
        flex: 1 1;
        display: inline-block;
        min-width: 200px;
        max-width: 100%;
      }
      .icon-card-container {
        max-width: 100%;
        min-width: 299px;
      }
      .impact-card-progress {
        margin-top: var(--beam-ImpactOverview-impactCard-progress-marginTop);
      }
      .shareButtonContainer {
        position: sticky;
        bottom: 0;
        z-index: 999;
        background-color: var(--beam-ImpactOverview-impactCard-shareButton-background);
        box-shadow: var(--beam-ImpactOverview-impactCard-shareButton-boxShadow);
      }
      .title {
        ${useCustomText("--beam-ImpactOverview-title")}
      }
      .subtitle {
        ${useCustomText("--beam-ImpactOverview-subtitle")}
      }
      .impact-card-title {
        ${useCustomText("--beam-ImpactOverview-impactCard-title")}
      }
      .impact-card-progressText {
        ${useCustomText("--beam-ImpactOverview-impactCard-progressText")}
      }
      .impact-card-description {
        ${useCustomText("--beam-ImpactOverview-impactCard-description")}
      }
      .impact-link {
        ${useCustomText("--beam-ImpactOverview-impactCard-link")}
      }
      .impact-link-arrow {
        display: var(--beam-ImpactOverview-impactCard-linkArrow-display, "inline");
      }
      .impact-link a,
      .impact-link a:visited {
        color: var(--beam-ImpactOverview-impactCard-link-color, inherit);
      }
      .impact-card-goalCompletionText {
        ${useCustomText("--beam-ImpactOverview-goalCompletionText")}
      }
      .footer {
        text-align: center;
      }
    `,
    css`
      .button {
        cursor: pointer;
      }
      .icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .plain-button {
        border: none;
        background: none;
        font-size: inherit;
        font-family: inherit;
      }
    `,
  ];
}

defineCustomElement(BeamImpactOverview);

declare global {
  interface HTMLElementTagNameMap {
    "beam-impact-overview": BeamImpactOverview;
  }
}
